import type { DiarySondagePhoto } from '@ast-data/shared';
import { leadingNumber } from './diaryNumbers';
import { diaryDay } from './diaryDuplicates';

/**
 * The Sondagen of a Tagebuch day with the Teilfoto taken of each — the editable
 * form of the doc's `sondagePhotos`, plus the two readings the rest of the app
 * still asks of a day. Free of React, so the dialog is left with the editing.
 */

/** One editable Sondage/Teilfoto row. The id is local — for stable list keys. */
export interface SondagePhotoRow {
    id: string;
    sondage: string;
    partialPhoto: string;
}

/**
 * What identifies an entry ACROSS days: its Sondage, or — for a Teilfoto of
 * something that is no Sondage — the photo. Written down again the next day it
 * is the same Sondage, however the other half was edited in between.
 */
export function sondagePhotoKey(entry: DiarySondagePhoto): string {
    const sondage = entry.sondage.trim().toLowerCase();
    return sondage !== ''
        ? `s:${sondage}`
        : `p:${entry.partialPhoto.trim().toLowerCase()}`;
}

/**
 * What is actually said — a row somebody added and left is not an entry. Takes
 * the bare pair rather than the editable row, so the same cleaning serves the
 * list being edited and a STORED list read back.
 */
export function filledSondagePhotos(
    rows: readonly DiarySondagePhoto[]
): DiarySondagePhoto[] {
    return rows
        .filter((row) => row.sondage.trim() || row.partialPhoto.trim())
        .map((row) => ({
            sondage: row.sondage.trim(),
            partialPhoto: row.partialPhoto.trim()
        }));
}

/**
 * The HIGHEST number the list names, as it was typed ("12a" stays "12a"). The
 * doc's `lastPartialPhotoNumber` means exactly this and is derived from the
 * list on save.
 *
 * Ordering is by the leading whole number — a suffix distinguishes records that
 * share a number, it does not order them (see `leadingNumber`). An entry with
 * no number at all wins when nothing numeric was named.
 */
function highestNumber(values: string[]): string {
    let best = '';
    let bestNumber: number | null = null;
    for (const raw of values) {
        const value = raw.trim();
        if (value === '') {
            continue;
        }
        const number = leadingNumber(value);
        if (number === null) {
            if (best === '') {
                best = value;
            }
            continue;
        }
        if (bestNumber === null || number > bestNumber) {
            best = value;
            bestNumber = number;
        }
    }
    return best;
}

/**
 * The day's last Teilfoto, derived from the list. The last Sondage is NOT: it is
 * typed as a field of its own (see DIARY_FORM_NUMBER_FIELDS).
 */
export function lastPartialPhotoNumber(rows: SondagePhotoRow[]): string {
    return highestNumber(
        filledSondagePhotos(rows).map((row) => row.partialPhoto)
    );
}

/** A Tagebuch day as the per-Sondage check reads it. */
export interface SondagePhotoDay {
    id: string;
    projectId: string;
    recordedAt: string;
    sondagePhotos: DiarySondagePhoto[];
}

/** One Sondage whose Teilfoto went backwards, with both readings and the day. */
export interface SondagePhotoWarning {
    sondage: string;
    value: string;
    previous: string;
    /** When the higher Teilfoto was recorded, as the stored timestamp. */
    date: string;
}

/**
 * The Sondagen whose Teilfoto is SMALLER than the last time the same Sondage
 * was written down — the one thing about this list that cannot be true.
 *
 * Photo numbers are handed out in order, so Sondage 2 with Teilfoto 3 on Monday
 * and Teilfoto 2 on Tuesday is a typo. Nothing else about the pair is checked:
 * a day may reopen an older Sondage, and the highest Teilfoto of two days
 * cannot be compared when they worked different Sondagen.
 *
 * Advisory, like every check in this form. Compared by the leading whole
 * number, since a suffix distinguishes records rather than ordering them.
 */
export function sondagePhotosWentBackwards(
    rows: readonly DiarySondagePhoto[],
    diaries: readonly SondagePhotoDay[],
    projectId: string,
    recordedAt: string,
    excludeId: string
): SondagePhotoWarning[] {
    const day = diaryDay(recordedAt);
    if (day === '') {
        return [];
    }
    // Every earlier day of the project, newest first — the first one naming a
    // Sondage is the last word on it.
    const earlier = diaries
        .filter((diary) => {
            if (diary.id === excludeId || diary.projectId !== projectId) {
                return false;
            }
            const other = diaryDay(diary.recordedAt);
            return other !== '' && other < day;
        })
        .sort((firstDiary, secondDiary) =>
            diaryDay(secondDiary.recordedAt).localeCompare(
                diaryDay(firstDiary.recordedAt)
            )
        );

    const warnings: SondagePhotoWarning[] = [];
    for (const row of filledSondagePhotos(rows)) {
        const photo = leadingNumber(row.partialPhoto);
        if (row.sondage === '' || photo === null) {
            continue;
        }
        const key = sondagePhotoKey(row);
        for (const diary of earlier) {
            const before = filledSondagePhotos(diary.sondagePhotos).find(
                (entry) =>
                    sondagePhotoKey(entry) === key &&
                    leadingNumber(entry.partialPhoto) !== null
            );
            if (!before) {
                continue;
            }
            // The last day that photographed this Sondage decides; an older one
            // is not consulted, so a correction made since then stands.
            if (leadingNumber(before.partialPhoto)! > photo) {
                warnings.push({
                    sondage: row.sondage,
                    value: row.partialPhoto,
                    previous: before.partialPhoto,
                    date: diary.recordedAt
                });
            }
            break;
        }
    }
    return warnings;
}
